import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Button, Stack, Step, StepConnector, StepLabel, Stepper, Tooltip, Typography, useMediaQuery, useTheme } from '@mui/material';
import { cloneDeep, get, set } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getFieldNamefromDataPath, isTrue } from 'src/utils/helper';
import { CheckIfUserAndOrganisationExist, RegisterUserAndOrganisation } from '../../../lib/api/AuthUser';
import { PAGES_URLS } from '../../../lib/constants/routes';
import ScrollToTop from '../../layout/ScrollToTop';
import LoadingBackdrop from '../../ui-component/loading/LoadingBackdrop';
import NoDetailsInfo from '../../ui-component/no-details-info/NoDetailsInfo';
import AbschlussRegistrierung from './abschluss/AbschlussRegistrierung';
import NachRegistrierung from './abschluss/NachRegistrierung';
import LoginDaten from './login-daten/LoginDaten';
import LoginDatenValidator from './login-daten/LoginDatenValidator';
import OrganisationDaten from './organisation-daten/OrganisationDaten';
import OrganisationDatenValidator from './organisation-daten/OrganistaionDatenValidator';
import PersonDaten from './person-daten/PersonDatan';
import PersonDatenValidator from './person-daten/PersonDatenValidator';

// import schemas
import OrgaSchema from '../../../lib/paths/organisation_FULL_Paths.json';
import PersonSchema from '../../../lib/paths/person_FULL_Paths.json';
import UserSchema from '../../../lib/paths/user_Paths.json';
const U_P = UserSchema.attributes;
const P_P = PersonSchema.attributes;
const OP_P = OrgaSchema.attributes;

const steps = [
	{
		label: 'Organisationsprofil',
		innerSteps: [],
	},
	{
		label: 'Ihr Profil',
		innerSteps: [],
	},

	{
		label: 'Ihre Zugangsdaten',
		innerSteps: [],
	},
	{
		label: 'Überprüfung',
		innerSteps: [],
	},
];

const innerStepsLabelStyle = {
	fontSize: '2rem',
};

const Register: FC<{}> = () => {
	const navigate = useNavigate();
	const location = useLocation();
	const registrierungAls = get(location, 'state.registrierenAls');

	const [scrollToTop, setScrollToTop] = useState(false);
	const [activeStep, setActiveStep] = useState(0);

	// Form States
	const [loading, setLoading] = useState(false);
	const [isRegistrationSuccess, setIsRegistrationSuccess] = useState<boolean | null>(null);
	const [formErrorMessage, setFormErrorMessage] = useState('');

	// Formvalues
	const [personDaten, setPersonDaten] = useState({});
	const [organisationDaten, setOrganisationDaten] = useState({});
	const [loginDaten, setLoginDaten] = useState({});
	const [datenschutzZustimmung, setDatenschutzZustimmung] = useState({
		zustimmung: false,
	});
	// Form Erros
	const [organisationDatenError, setOrganisationDatenError] = useState({});
	const [personDatenError, setPersonDatenError] = useState({});
	const [loginDatenError, setLoginDatenError] = useState({});
	const [datenschutzZustimmungError, setDatenschutzZustimmungError] = useState({});

	// handle update formvalues
	const handleUpdateOrganisationDaten = (key: string, value: any) => {
		// setHasFormEdited(true);
		let formValueCopy = cloneDeep(organisationDaten);
		if (key === OP_P.Unternehmensprofil_U_St_Id_Nr.path && typeof value === 'string') value = value.trim();
		set(formValueCopy, key, value);
		setOrganisationDaten(formValueCopy);
		let errorObject = cloneDeep(organisationDatenError);
		delete errorObject[key];
		setOrganisationDatenError(errorObject);
		setFormErrorMessage('');
		setScrollToTop(false);
	};
	const handleUpdatePersonDaten = (key: string, value: any) => {
		// setHasFormEdited(true);
		let formValueCopy = cloneDeep(personDaten);
		set(formValueCopy, key, value);
		setPersonDaten(formValueCopy);
		let errorObject = cloneDeep(personDatenError);
		delete errorObject[key];
		setPersonDatenError(errorObject);

		if (get(formValueCopy, P_P.Kontaktdaten_E_Mail.path, '')) {
			const contactEmail = get(formValueCopy, P_P.Kontaktdaten_E_Mail.path, '');
			setLoginDaten((prevValue: any) => ({
				...prevValue,
				[U_P.email.path]: contactEmail,
			}));
			setOrganisationDaten((prevValue: any) => {
				const updatedValue = cloneDeep(prevValue);
				const firstMailbenachrichtigungPath = `${OP_P.Mailbenachrichtungen_Mail.path}.0.${getFieldNamefromDataPath(OP_P.Mailbenachrichtungen_Mail.schema.attributes.Mailbenachrichtungen_Mail.path)}`;
				set(updatedValue, firstMailbenachrichtigungPath, contactEmail);
				return updatedValue;
			});
		}
		setFormErrorMessage('');
		setScrollToTop(false);
	};
	const handleUpdateLoginDaten = (key: string, value: any) => {
		// setHasFormEdited(true);
		if (key === U_P.username.path && typeof value === 'string') value = value.trim();
		let formValueCopy = cloneDeep(loginDaten);
		set(formValueCopy, key, value);
		setLoginDaten(formValueCopy);
		let errorObject = cloneDeep(loginDatenError);
		delete errorObject[key];
		setLoginDatenError(errorObject);
		setFormErrorMessage('');
		setScrollToTop(false);
	};
	const handleUpdateDatenschutzZustimmung = (key: string, value: any) => {
		let formValueCopy = cloneDeep(datenschutzZustimmung);
		set(formValueCopy, key, value);

		if (key === OP_P.Geschaeftsbezogene_Daten_Zustimmung_Erhalt_Newsletter.path) {
			handleUpdateOrganisationDaten(key, value);
		}
		setDatenschutzZustimmung(formValueCopy);
		let errorObject = cloneDeep(datenschutzZustimmungError);
		delete errorObject[key];
		setDatenschutzZustimmungError(errorObject);
		setFormErrorMessage('');
		setScrollToTop(false);
	};

	// Handle Validations
	const validateOrganisationData = () => {
		const orgaCopy = cloneDeep(organisationDaten);
		let errorObject = {};
		let errs = OrganisationDatenValidator(orgaCopy).validate(orgaCopy);
		for (let error of errs) {
			set(errorObject, error.path, get(error, 'message'));
		}
		setOrganisationDatenError(errorObject);
		return Object.keys(errorObject).length === 0;
	};
	const validatePersonData = () => {
		let errorObject = {};
		let errs = PersonDatenValidator.validate(personDaten);
		for (let error of errs) {
			set(errorObject, error.path, get(error, 'message'));
		}
		setPersonDatenError(errorObject);
		return Object.keys(errorObject).length === 0;
	};
	const validateLoginData = () => {
		let errorObject = {};
		let errs = LoginDatenValidator.validate(loginDaten);
		for (let error of errs) {
			set(errorObject, error.path, get(error, 'message'));
		}
		setLoginDatenError(errorObject);
		return Object.keys(errorObject).length === 0;
	};
	const validateAbschlussRegistrierung = () => {
		if (isTrue(datenschutzZustimmung.zustimmung)) {
			return true;
		}

		if (!isTrue(datenschutzZustimmung.zustimmung)) {
			let errorObject = {};
			set(errorObject, 'zustimmung', 'Sie müssen den Datenschutzhinweisen zum digitalen Antragsportal zustimmen.');
			setDatenschutzZustimmungError(errorObject);
			return false;
		}
	};

	const handleBack = () => {
		setScrollToTop(true);
		setActiveStep(activeStep - 1);
	};

	const handleNext = async () => {
		setScrollToTop(true);
		switch (activeStep) {
			case 0:
				if (validateOrganisationData()) {
					/**
					 * Laut das Ticket HA-2218 muss die Organisation nicht geprüft werden.
					 * Aber ich bezweifle, dass es gut ist, die Organisation nicht zu prüfen, weil es zu Problemen in nächsten Schritten führen kann, wenn die
					 * Organisation schon existiert. Deswegen habe ich die Prüfung erstmal drin gelassen, aber auskommentiert. Wenn es entschieden wird, dass die
					 * Prüfung wirklich nicht notwendig ist, kann der Code einfach gelöscht werden.
					 * *************************************************************  BITTE LESEN  **************************************************************
					 * DIESE PRÜFUNG WIRD AUCH UNTEN IN DER CASE: 2 FÜR DIE PERSON DURCHGEFÜHRT. DORT IST ES AUCH AUSKOMMENTIERT.
					 */
					// try {
					// 	await CheckIfUserAndOrganisationExist(organisationDaten);
					setActiveStep(1);
					// } catch (err: any) {
					// 	console.error('Error checking Organisation ', err);
					// 	setFormErrorMessage('Organisation existiert bereit');
					// }
				}
				break;
			case 1:
				if (validatePersonData()) {
					setActiveStep(2);
				}
				break;
			case 2:
				if (validateLoginData()) {
					if (get(loginDaten, U_P.password.path) !== get(loginDaten, U_P.repeatPassword.path)) {
						setLoginDatenError((prevValue) => ({
							...prevValue,
							[U_P.repeatPassword.path]: 'Beide Passwörter stimmen nicht überein.',
						}));
					} else {
						try {
							await CheckIfUserAndOrganisationExist(organisationDaten, loginDaten);
							setActiveStep(3);
						} catch (err: any) {
							console.error('Error checking Perosn', err);
							setFormErrorMessage('Person existiert bereit');
						}
					}
				}
				break;
			default:
				break;
		}
	};

	const handleReigstration = async () => {
		try {
			setScrollToTop(true);
			if (validateAbschlussRegistrierung()) {
				setLoading(true);
				let res = await RegisterUserAndOrganisation(organisationDaten, loginDaten, personDaten);
				if (res) {
					setIsRegistrationSuccess(true);
					setActiveStep(4);
				}
			} else {
				setFormErrorMessage('Sie müssen die Datenschutzhinweise zum digitalen Antragsportal zustimmen.');
				setActiveStep(3);
			}
		} catch (err: any) {
			setIsRegistrationSuccess(false);
			console.error(' ERROR: Registration ', err);
		} finally {
			setLoading(false);
		}
	};

	// Verhindert, die Seite zu verlassen, wenn der Benutzer mitten im Registrierungsprozess ist
	useEffect(() => {
		const unloadCallback = (event: any) => {
			event.preventDefault();
			event.returnValue = '';
			return '';
		};
		window.addEventListener('beforeunload', unloadCallback);
		return () => window.removeEventListener('beforeunload', unloadCallback);
	}, []);

	if (registrierungAls !== 'KMU') {
		return <NoDetailsInfo title='Registrierungsoption' goBackUrl={PAGES_URLS.RegistrierungVorschritt} />;
	}

	return (
		<>
			<LoadingBackdrop open={loading} />
			{scrollToTop && <ScrollToTop />}
			<Box
				sx={{
					display: 'flex',
					width: '100%',
					padding: '0 1.875rem',
					margin: '2rem auto 0',
					boxSizing: 'border-box',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						justifyContent: 'space-between',
						alignItems: { xs: 'stretch', xl: 'baseline' },
						width: '100%',
						minHeight: '100px',
						flexDirection: { xs: 'column', lg: 'row' },
					}}
				>
					<Box
						sx={{
							textAlign: 'left',
							marginBottom: '2rem',
							display: activeStep === 4 ? 'none' : 'flex',
							flex: 1,
							flexDirection: 'column',
							alignItems: { xs: 'center', lg: 'flex-end' },
						}}
					>
						{!isRegistrationSuccess && (
							<Button
								variant='outlined'
								startIcon={<ArrowBackIcon />}
								onClick={() => navigate(PAGES_URLS.Login)}
								sx={{ m: '1rem 2rem 1rem 0', minWidth: '16.4rem' }}
							>
								Zurück zum Login
							</Button>
						)}
						<FormStepper activeStep={activeStep} />
					</Box>
					<Box sx={{ flex: '1 1 71.25rem', display: 'flex', justifyContent: activeStep === 4 ? 'center' : 'flex-start' }}>
						<Box
							component='form'
							sx={{
								width: '100%',
								maxWidth: '71.25rem',
								backgroundColor: '#fff',
								padding: '0 2rem 2rem',
							}}
						>
							{/* Step 1: Organisationsdaten */}
							{activeStep === 0 && (
								<>
									<OrganisationDaten
										formValue={organisationDaten}
										updateFunction={handleUpdateOrganisationDaten}
										organisationDatenError={organisationDatenError}
										formErrorMessage={formErrorMessage}
									/>
								</>
							)}
							{/* Step 2: Personendaten */}
							{activeStep === 1 && (
								<>
									<PersonDaten formValue={personDaten} updateFunction={handleUpdatePersonDaten} personDatenError={personDatenError} />
								</>
							)}
							{/* Step 3: Login / Zugangsdaten */}
							{activeStep === 2 && (
								<>
									<LoginDaten
										formValue={loginDaten}
										updateFunction={handleUpdateLoginDaten}
										loginDatenError={loginDatenError}
										formErrorMessage={formErrorMessage}
									/>
								</>
							)}
							{/* Step 4: Abschluss */}
							{activeStep === 3 && (
								<>
									<AbschlussRegistrierung
										formValue={datenschutzZustimmung}
										updateFunction={handleUpdateDatenschutzZustimmung}
										datenschutzZustimmungError={datenschutzZustimmungError}
										formErrorMessage={formErrorMessage}
										personDaten={personDaten}
										organisationDaten={organisationDaten}
										LoginDaten={loginDaten}
									/>
								</>
							)}
							{/* Step 5: Erfolg Meldung Seite */}
							{activeStep === 4 && <NachRegistrierung isRegistrationSuccess={isRegistrationSuccess} />}

							{/* * Logics for Navigation Buttons*/}
							<Box sx={{ mt: '3rem' }}>
								{activeStep === 3 ?
									<Stack direction='row' justifyContent='space-between'>
										<Button variant='outlined' onClick={handleBack}>
											Zurück
										</Button>
										<Button variant='contained' onClick={handleReigstration}>
											Jetzt registrieren
										</Button>
									</Stack>
								:	<React.Fragment>
										{activeStep < 3 && (
											<Stack direction={'row'} justifyContent={activeStep === 0 ? 'flex-end' : 'space-between'}>
												{activeStep > 0 && (
													<Button variant='outlined' disabled={activeStep === 0} onClick={handleBack}>
														Zurück
													</Button>
												)}
												<Button variant='contained' onClick={handleNext}>
													Weiter
												</Button>
											</Stack>
										)}
									</React.Fragment>
								}
							</Box>
						</Box>
					</Box>
				</Box>
			</Box>
		</>
	);
};

export default Register;

const FormStepper: FC<{ activeStep: number }> = ({ activeStep }) => {
	const theme = useTheme();
	const isXlUp = useMediaQuery(theme.breakpoints.up('lg'));

	return (
		<Box sx={{ minWidth: '16rem', margin: '1rem 2rem 1rem 0' }}>
			<Stepper
				alternativeLabel={!isXlUp}
				activeStep={activeStep}
				orientation={isXlUp ? 'vertical' : 'horizontal'}
				sx={{
					width: '100%',
					'& .MuiStepConnector-line': {
						borderColor: 'primary.main',
						borderWidth: '1.5px',
					},
				}}
			>
				{steps.map((ele: any, i: number) => {
					const stepLabel = (
						<StepLabel
							StepIconProps={{
								sx: {
									color: '#fff',
									'& .MuiStepIcon-text': {
										display: isXlUp ? 'none' : 'block',
										fill: '#023671',
										fontWeight: 'bold',
									},
									'&.MuiSvgIcon-root.Mui-active': {
										bgcolor: '#023671',
									},
									'&.MuiStepIcon-root.Mui-completed': {
										bgcolor: '#023671',
									},
									borderRadius: '50%',
									border: '1.5px solid #023671',
								},
							}}
						>
							{isXlUp && (
								<Typography
									variant='body2'
									sx={{
										fontFeatureSettings: "'liga' off, 'clig' off",
										fontFamily: '"Avenir Next LT Pro"',
										fontStyle: 'normal',
										fontSize: { md: '.8rem', lg: '0.875rem' },
									}}
								>
									{ele.label}
								</Typography>
							)}
						</StepLabel>
					);

					return (
						<Step key={i}>
							{isXlUp ?
								stepLabel
							:	<Tooltip title={ele.label} arrow>
									{stepLabel}
								</Tooltip>
							}
							<Box>
								<Stepper activeStep={i} orientation='vertical'>
									{ele.innerSteps.map((label: string, index: number) => {
										const stepProps = {};
										const labelProps = {};

										return (
											<Step key={index} {...stepProps}>
												<StepLabel {...labelProps}>
													<Typography sx={innerStepsLabelStyle}>{label}</Typography>
												</StepLabel>
												<StepConnector
													sx={{
														display: 'none',
													}}
												></StepConnector>
											</Step>
										);
									})}
								</Stepper>
							</Box>
						</Step>
					);
				})}
			</Stepper>
		</Box>
	);
};
